import { useEffect, useState } from 'react';

/** Simulated streaming: reveal `text` one character at a time. */
export function useTypewriter(
  text: string,
  opts: { charDelayMs?: number; enabled?: boolean } = {},
): { displayText: string; isComplete: boolean } {
  const { charDelayMs = 14, enabled = true } = opts;
  const [displayText, setDisplayText] = useState<string>(() => (enabled ? '' : text));
  const [isComplete, setIsComplete] = useState<boolean>(() => !enabled || !text);

  useEffect(() => {
    if (!enabled || !text) {
      setDisplayText(text || '');
      setIsComplete(true);
      return;
    }

    setDisplayText('');
    setIsComplete(false);

    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setDisplayText(text.slice(0, i));
      if (i >= text.length) {
        window.clearInterval(id);
        setIsComplete(true);
      }
    }, charDelayMs);

    return () => window.clearInterval(id);
  }, [text, charDelayMs, enabled]);

  return { displayText, isComplete };
}

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = () => setReduced(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return reduced;
}
